import { defineStore } from 'pinia';
import { computed } from 'vue';
import { extensionsFlag } from '#layers/base/public';
import { ExtensionEntryType } from '#layers/extensions/types/catalog';
import { defineWorkspaceHydration } from '#layers/runtime/public';

/**
 * The extension catalog of a workspace runtime.
 *
 * Read without an entitlement check: the protected `studio_access` policy grants `extension:read` in
 * every workspace, and a candidate cannot evaluate entitlements anyway because the permissions store
 * follows the active runtime.
 */
export const workspaceExtensionsHydration = defineWorkspaceHydration(
	(store) => store.WorkspaceExtension.query((q) => q.many({ enabled: extensionsFlag.value })),
	{ empty: [] },
);

/**
 * The connectors those extensions contribute, as data source providers.
 *
 * Optional: `connectors/data` requires `dataSource:create`, which `studio_access` does not grant, so
 * it 403s every member who cannot create a data source. Loading it with the runtime keeps the picker
 * warm for those who can, and the failure leaves an empty catalog for those who cannot — who never
 * reach the picker, because the same entitlement gates it.
 */
export const workspaceDataConnectorsHydration = defineWorkspaceHydration(
	(store) => store.WorkspaceDataConnector.query((q) => q.many({ enabled: extensionsFlag.value })),
	{ empty: [], optional: true },
);

export const useExtensionsStore = defineStore('extensions', () => {
	const { data: extensionCatalog } = workspaceExtensionsHydration.use();
	const { data: connectorCatalog } = workspaceDataConnectorsHydration.use();

	const extensions = computed(() => extensionsFlag.value ? extensionCatalog.value : []);
	const dataConnectors = computed(() => extensionsFlag.value
		? connectorCatalog.value.filter((connector) => connector.extensionId)
		: []);

	const dataConnectorProviderIds = computed(() => extensions.value.flatMap(({ entries }) => entries
		.filter(({ type }) => type === ExtensionEntryType.DataConnector)
		.map(({ id }) => id)));

	return {
		extensions,
		dataConnectors,
		dataConnectorProviderIds,
	};
});
